/**
 * TanStack Query mutation hooks for shift operations
 *
 * All mutations implement optimistic updates for instant UI feedback.
 * Changes appear immediately while syncing in the background.
 * On error, changes are automatically rolled back.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UnifiedShiftService, ShiftCreateParams, ShiftUpdateParams, UnifiedShiftQueryResult } from '@/lib/scheduling/unified-shift-service';
import { DayShift } from '@/types';
import { shiftQueryKeys } from './useShiftsQuery';
import { useShiftStatusService } from './useShiftStatusService';
import { employeeAvailabilityCache } from '@/lib/employees/EmployeeAvailabilityCache';
import { toast } from '@/hooks/ui/use-toast';
import { ShiftActivityLogger } from '@/lib/scheduling/shift-activity-logger';
import { planFulfillmentKeys } from '@/hooks/employees/usePlanFulfillment';

interface MutationContext {
  previousData: Map<string, UnifiedShiftQueryResult | undefined>;
}

interface UpdateMutationContext extends MutationContext {
  previousShift?: DayShift;
}

interface DeleteMutationContext extends MutationContext {
  deletedShift?: DayShift;
}

interface AssignMutationContext extends MutationContext {
  oldEmployeeId?: string | null;
  oldEmployeeName?: string | null;
  shiftData?: { businessId: string; shiftDate: Date; shiftTitle: string | null };
}

/**
 * Hook for creating shifts with optimistic updates
 */
export function useCreateShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: UnifiedShiftService.createShift,

    onMutate: async (params: ShiftCreateParams): Promise<MutationContext> => {
      // Cancel any outgoing refetches to prevent overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: shiftQueryKeys.all });

      // Snapshot all current shift queries for potential rollback
      const previousData = new Map<string, UnifiedShiftQueryResult | undefined>();
      const queries = queryClient.getQueriesData<UnifiedShiftQueryResult>({
        queryKey: shiftQueryKeys.lists(),
      });

      queries.forEach(([queryKey, data]) => {
        previousData.set(JSON.stringify(queryKey), data);
      });

      // Create optimistic shift
      const optimisticShift: DayShift = {
        id: `temp-${Date.now()}`, // Temporary ID
        business_id: params.businessId,
        employeeId: params.employeeId || null,
        employeeName: '', // Will be filled after server response
        startTime: params.startTime,
        endTime: params.endTime,
        title: params.title,
        notes: params.notes || '',
        isNew: true,
        isEditing: false,
        isHistorical: false,
        requestCount: 0,
        breaks: [],
        calculatedStatus: params.employeeId ? 'scheduled' : 'unassigned',
        source: 'manual',
      };

      // Add to all relevant cached queries
      queries.forEach(([queryKey, data]) => {
        if (data) {
          const [, , , businessId, startDate, endDate] = queryKey as string[];

          // Check if shift falls within this query's date range
          const queryStart = new Date(startDate);
          const queryEnd = new Date(endDate);

          if (
            businessId === params.businessId &&
            params.startTime >= queryStart &&
            params.startTime <= queryEnd
          ) {
            queryClient.setQueryData<UnifiedShiftQueryResult>(queryKey, {
              ...data,
              shifts: [...data.shifts, optimisticShift].sort(
                (a, b) => a.startTime.getTime() - b.startTime.getTime()
              ),
              totalCount: data.totalCount + 1,
              currentCount: data.currentCount + 1,
            });
          }
        }
      });

      return { previousData };
    },

    onError: (error, params, context) => {
      // Rollback to previous state on error
      if (context?.previousData) {
        context.previousData.forEach((data, key) => {
          const queryKey = JSON.parse(key);
          queryClient.setQueryData(queryKey, data);
        });
      }

      toast.error('Failed to create shift', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },

    onSuccess: async (newShift, params) => {
      // Invalidate to get the real shift with proper ID and employee name
      employeeAvailabilityCache.invalidateCache('shift-created');

      // Log shift creation activity
      const userId = await ShiftActivityLogger.getCurrentUserId();
      if (userId && newShift) {
        ShiftActivityLogger.logShiftCreated(
          params.businessId,
          {
            id: newShift.id,
            title: newShift.title,
            startTime: newShift.startTime,
            endTime: newShift.endTime,
            employeeId: newShift.employeeId,
          },
          userId,
          'calendar'
        );
      }
    },

    onSettled: () => {
      // Always refetch in background to ensure consistency
      queryClient.invalidateQueries({ queryKey: shiftQueryKeys.all });
      // Invalidate plan fulfillment to update hours planned
      queryClient.invalidateQueries({ queryKey: planFulfillmentKeys.all });
    },
  });
}

/**
 * Hook for updating shifts with optimistic updates
 */
export function useUpdateShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: UnifiedShiftService.updateShift,

    onMutate: async (params: ShiftUpdateParams): Promise<UpdateMutationContext> => {
      await queryClient.cancelQueries({ queryKey: shiftQueryKeys.all });

      const previousData = new Map<string, UnifiedShiftQueryResult | undefined>();
      let previousShift: DayShift | undefined;

      const queries = queryClient.getQueriesData<UnifiedShiftQueryResult>({
        queryKey: shiftQueryKeys.lists(),
      });

      queries.forEach(([queryKey, data]) => {
        previousData.set(JSON.stringify(queryKey), data);

        if (data) {
          // Capture the previous shift data for logging
          if (!previousShift) {
            previousShift = data.shifts.find((s) => s.id === params.id);
          }

          queryClient.setQueryData<UnifiedShiftQueryResult>(queryKey, {
            ...data,
            shifts: data.shifts.map((shift) => {
              if (shift.id === params.id) {
                return {
                  ...shift,
                  startTime: params.startTime || shift.startTime,
                  endTime: params.endTime || shift.endTime,
                  title: params.title !== undefined ? params.title : shift.title,
                  employeeId: params.employeeId !== undefined ? params.employeeId : shift.employeeId,
                  notes: params.notes !== undefined ? params.notes : shift.notes,
                  breaks: params.breaks !== undefined ? params.breaks : shift.breaks,
                };
              }
              return shift;
            }),
          });
        }
      });

      return { previousData, previousShift };
    },

    onError: (error, params, context) => {
      if (context?.previousData) {
        context.previousData.forEach((data, key) => {
          const queryKey = JSON.parse(key);
          queryClient.setQueryData(queryKey, data);
        });
      }

      toast.error('Failed to update shift', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },

    onSuccess: async (updatedShift, params, context) => {
      employeeAvailabilityCache.invalidateCache('shift-updated');

      // Log shift update activity
      const userId = await ShiftActivityLogger.getCurrentUserId();
      const prevShift = context?.previousShift;

      if (userId && prevShift && updatedShift) {
        // Detect if this was a time change (move) or content change (edit)
        const timeChanged =
          (params.startTime && params.startTime.getTime() !== prevShift.startTime.getTime()) ||
          (params.endTime && params.endTime.getTime() !== prevShift.endTime.getTime());

        const contentChanged =
          (params.title !== undefined && params.title !== prevShift.title) ||
          (params.notes !== undefined && params.notes !== prevShift.notes);

        if (timeChanged) {
          // Log as move
          ShiftActivityLogger.logShiftMoved(
            updatedShift.business_id,
            updatedShift.id,
            updatedShift.title,
            prevShift.startTime,
            prevShift.endTime,
            params.startTime || prevShift.startTime,
            params.endTime || prevShift.endTime,
            userId
          );
        } else if (contentChanged) {
          // Log as edit
          const changes: { title?: { old: string | null; new: string | null }; notes?: { old: string | null; new: string | null } } = {};
          if (params.title !== undefined && params.title !== prevShift.title) {
            changes.title = { old: prevShift.title || null, new: params.title || null };
          }
          if (params.notes !== undefined && params.notes !== prevShift.notes) {
            changes.notes = { old: prevShift.notes || null, new: params.notes || null };
          }

          ShiftActivityLogger.logShiftEdited(
            updatedShift.business_id,
            updatedShift.id,
            updatedShift.startTime,
            updatedShift.title,
            changes,
            userId
          );
        }
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: shiftQueryKeys.all });
      // Invalidate plan fulfillment to update hours planned
      queryClient.invalidateQueries({ queryKey: planFulfillmentKeys.all });
    },
  });
}

/**
 * Hook for deleting shifts with optimistic updates
 */
export function useDeleteShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: UnifiedShiftService.deleteShift,

    onMutate: async (shiftId: string): Promise<DeleteMutationContext> => {
      await queryClient.cancelQueries({ queryKey: shiftQueryKeys.all });

      const previousData = new Map<string, UnifiedShiftQueryResult | undefined>();
      let deletedShift: DayShift | undefined;

      const queries = queryClient.getQueriesData<UnifiedShiftQueryResult>({
        queryKey: shiftQueryKeys.lists(),
      });

      queries.forEach(([queryKey, data]) => {
        previousData.set(JSON.stringify(queryKey), data);

        if (data) {
          const shiftToDelete = data.shifts.find(s => s.id === shiftId);
          // Capture the shift being deleted for logging
          if (!deletedShift && shiftToDelete) {
            deletedShift = shiftToDelete;
          }

          queryClient.setQueryData<UnifiedShiftQueryResult>(queryKey, {
            ...data,
            shifts: data.shifts.filter((shift) => shift.id !== shiftId),
            totalCount: data.totalCount - (shiftToDelete ? 1 : 0),
            currentCount: data.currentCount - (shiftToDelete && !shiftToDelete.isHistorical ? 1 : 0),
            historicalCount: data.historicalCount - (shiftToDelete && shiftToDelete.isHistorical ? 1 : 0),
          });
        }
      });

      return { previousData, deletedShift };
    },

    onError: (error, shiftId, context) => {
      if (context?.previousData) {
        context.previousData.forEach((data, key) => {
          const queryKey = JSON.parse(key);
          queryClient.setQueryData(queryKey, data);
        });
      }

      toast.error('Failed to delete shift', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },

    onSuccess: async (_result, shiftId, context) => {
      employeeAvailabilityCache.invalidateCache('shift-deleted');

      // Log shift deletion activity
      const userId = await ShiftActivityLogger.getCurrentUserId();
      const deletedShift = context?.deletedShift;

      if (userId && deletedShift) {
        ShiftActivityLogger.logShiftDeleted(
          deletedShift.business_id,
          shiftId,
          deletedShift.title || null,
          deletedShift.startTime,
          deletedShift.startTime,
          deletedShift.endTime,
          deletedShift.employeeId,
          userId
        );
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: shiftQueryKeys.all });
      // Invalidate plan fulfillment to update hours planned
      queryClient.invalidateQueries({ queryKey: planFulfillmentKeys.all });
    },
  });
}

/**
 * Hook for assigning employees to shifts with optimistic updates
 */
export function useAssignEmployee() {
  const queryClient = useQueryClient();
  const shiftStatusService = useShiftStatusService();

  return useMutation({
    mutationFn: ({
      shiftId,
      employeeId,
    }: {
      shiftId: string;
      employeeId: string | null;
    }) => UnifiedShiftService.assignEmployeeToShift(shiftId, employeeId),

    onMutate: async ({
      shiftId,
      employeeId,
    }): Promise<AssignMutationContext> => {
      await queryClient.cancelQueries({ queryKey: shiftQueryKeys.all });

      const previousData = new Map<string, UnifiedShiftQueryResult | undefined>();
      let oldEmployeeId: string | null | undefined;
      let oldEmployeeName: string | null | undefined;
      let shiftData: { businessId: string; shiftDate: Date; shiftTitle: string | null } | undefined;

      const queries = queryClient.getQueriesData<UnifiedShiftQueryResult>({
        queryKey: shiftQueryKeys.lists(),
      });

      queries.forEach(([queryKey, data]) => {
        previousData.set(JSON.stringify(queryKey), data);

        if (data) {
          queryClient.setQueryData<UnifiedShiftQueryResult>(queryKey, {
            ...data,
            shifts: data.shifts.map((shift) => {
              if (shift.id === shiftId) {
                // Capture previous assignment info for logging
                if (oldEmployeeId === undefined) {
                  oldEmployeeId = shift.employeeId;
                  oldEmployeeName = shift.employeeName;
                  shiftData = {
                    businessId: shift.business_id,
                    shiftDate: shift.startTime,
                    shiftTitle: shift.title || null,
                  };
                }

                // Recalculate status based on new assignment (using timezone-aware service)
                const newStatus = shiftStatusService.calculateShiftStatus(
                  shift.startTime,
                  shift.endTime,
                  employeeId,
                  shift.actualArrivalTime || undefined,
                  shift.actualDepartureTime || undefined
                );

                return {
                  ...shift,
                  employeeId,
                  employeeName: '', // Will be updated after server response
                  calculatedStatus: newStatus,
                };
              }
              return shift;
            }),
          });
        }
      });

      return { previousData, oldEmployeeId, oldEmployeeName, shiftData };
    },

    onError: (error, params, context) => {
      if (context?.previousData) {
        context.previousData.forEach((data, key) => {
          const queryKey = JSON.parse(key);
          queryClient.setQueryData(queryKey, data);
        });
      }

      toast.error('Failed to assign employee', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },

    onSuccess: async (updatedShift, { shiftId, employeeId }, context) => {
      // Invalidate availability cache for affected employees
      if (employeeId) {
        employeeAvailabilityCache.invalidateCache('employee-assigned', [employeeId]);
      } else {
        employeeAvailabilityCache.invalidateCache('employee-unassigned');
      }

      // Log assignment activity
      const userId = await ShiftActivityLogger.getCurrentUserId();
      const shiftData = context?.shiftData;

      if (userId && shiftData && updatedShift) {
        if (employeeId) {
          // Employee was assigned
          ShiftActivityLogger.logEmployeeAssigned(
            shiftData.businessId,
            shiftId,
            shiftData.shiftDate,
            shiftData.shiftTitle,
            employeeId,
            updatedShift.employeeName || null,
            context?.oldEmployeeId || null,
            context?.oldEmployeeName || null,
            userId
          );
        } else if (context?.oldEmployeeId) {
          // Employee was unassigned
          ShiftActivityLogger.logEmployeeUnassigned(
            shiftData.businessId,
            shiftId,
            shiftData.shiftDate,
            shiftData.shiftTitle,
            context.oldEmployeeId,
            context.oldEmployeeName || null,
            userId
          );
        }
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: shiftQueryKeys.all });
      // Invalidate plan fulfillment to update hours planned for affected employees
      queryClient.invalidateQueries({ queryKey: planFulfillmentKeys.all });
    },
  });
}

/**
 * Hook for batch assigning employees to multiple shifts
 */
export function useBatchAssignEmployee() {
  const queryClient = useQueryClient();
  const shiftStatusService = useShiftStatusService();

  return useMutation({
    mutationFn: async ({
      shiftIds,
      employeeId,
    }: {
      shiftIds: string[];
      employeeId: string | null;
    }) => {
      // Process all assignments
      const results = await Promise.all(
        shiftIds.map((shiftId) =>
          UnifiedShiftService.assignEmployeeToShift(shiftId, employeeId)
        )
      );
      return results;
    },

    onMutate: async ({ shiftIds, employeeId }): Promise<MutationContext> => {
      await queryClient.cancelQueries({ queryKey: shiftQueryKeys.all });

      const previousData = new Map<string, UnifiedShiftQueryResult | undefined>();
      const queries = queryClient.getQueriesData<UnifiedShiftQueryResult>({
        queryKey: shiftQueryKeys.lists(),
      });

      queries.forEach(([queryKey, data]) => {
        previousData.set(JSON.stringify(queryKey), data);

        if (data) {
          queryClient.setQueryData<UnifiedShiftQueryResult>(queryKey, {
            ...data,
            shifts: data.shifts.map((shift) => {
              if (shiftIds.includes(shift.id)) {
                const newStatus = shiftStatusService.calculateShiftStatus(
                  shift.startTime,
                  shift.endTime,
                  employeeId,
                  shift.actualArrivalTime || undefined,
                  shift.actualDepartureTime || undefined
                );

                return {
                  ...shift,
                  employeeId,
                  employeeName: '',
                  calculatedStatus: newStatus,
                };
              }
              return shift;
            }),
          });
        }
      });

      return { previousData };
    },

    onError: (error, params, context) => {
      if (context?.previousData) {
        context.previousData.forEach((data, key) => {
          const queryKey = JSON.parse(key);
          queryClient.setQueryData(queryKey, data);
        });
      }

      toast.error('Failed to batch assign employees', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },

    onSuccess: () => {
      employeeAvailabilityCache.invalidateCache('batch-assign');
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: shiftQueryKeys.all });
      // Invalidate plan fulfillment to update hours planned for affected employees
      queryClient.invalidateQueries({ queryKey: planFulfillmentKeys.all });
    },
  });
}

/**
 * Convenience hook that provides all shift mutations
 */
export function useShiftMutations() {
  const createShift = useCreateShift();
  const updateShift = useUpdateShift();
  const deleteShift = useDeleteShift();
  const assignEmployee = useAssignEmployee();
  const batchAssignEmployee = useBatchAssignEmployee();

  return {
    createShift,
    updateShift,
    deleteShift,
    assignEmployee,
    batchAssignEmployee,
    // Convenience flags
    isAnyMutating:
      createShift.isPending ||
      updateShift.isPending ||
      deleteShift.isPending ||
      assignEmployee.isPending ||
      batchAssignEmployee.isPending,
  };
}
